import type { ForecastSku } from '@shared/types';

/**
 * Los pasos del recorrido, como DATOS.
 *
 * Separados de la pantalla a propósito: cada paso es una **premisa, una acción real y una
 * medición**, y tenerlos como una lista hace que agregar uno sea agregar un objeto —
 * no tocar el componente. También los hace leíbles de un vistazo, que es la mitad de por
 * qué el recorrido sirve.
 *
 * ⚠️ **Ninguno de estos pasos simula nada.** Cada `hacer` llama a la API de verdad y el
 * resultado se vuelve a LEER de la base. Un recorrido con números escritos a mano sería
 * una presentación, no una prueba — y lo que se está tratando de contestar es justamente
 * si el motor hace lo que dice.
 */

export type Medicion = {
  /** Qué se está midiendo, en castellano. */
  concepto: string;
  antes: number;
  despues: number;
  /** Cómo se escribe el número (unidades, días, %). */
  sufijo?: string;
};

export type Contexto = {
  /** El forecast recién leído, por SKU. */
  porSku: Record<string, ForecastSku | undefined>;
  settings: { serviceLevel: number; coverageTargetDays: number };
};

export type Paso = {
  id: string;
  titulo: string;
  /** Una o dos frases: qué se va a ver y por qué importa. */
  premisa: string;
  /** El texto del botón. Empieza con un verbo: es una acción, no un "siguiente". */
  accion: string;
  /** Qué se espera ver, escrito ANTES de apretar. Es lo que convierte esto en una prueba. */
  esperado: string;
  /** SKU sobre el que se mide, si aplica. */
  sku?: string;
  /** Devuelve las mediciones comparando el contexto de antes contra el de después. */
  medir: (antes: Contexto, despues: Contexto) => Medicion[];
};

const DEMO_1 = 'DEMO-1';
const DEMO_2 = 'DEMO-2';

const dato = (c: Contexto, sku: string, campo: keyof ForecastSku): number =>
  Number(c.porSku[sku]?.[campo] ?? 0);

/** Las mediciones típicas de un producto, para no repetirlas en cada paso. */
const medirProducto = (sku: string, campos: [keyof ForecastSku, string, string?][]) =>
  (a: Contexto, d: Contexto): Medicion[] =>
    campos.map(([campo, concepto, sufijo]) => ({
      concepto,
      antes: dato(a, sku, campo),
      despues: dato(d, sku, campo),
      sufijo,
    }));

export const PASOS: Paso[] = [
  {
    id: 'leer',
    titulo: 'De dónde sale el número',
    premisa:
      'DEMO-1 vende 10 unidades por día, todos los días. El motor promedia el último año, así que su demanda proyectada tiene que dar exactamente 10 — y con eso su punto de reorden sale de una multiplicación que se puede hacer con una calculadora.',
    accion: 'Leer lo que calculó el motor',
    esperado:
      'Demanda 10 por día y punto de reorden 300: los 30 días de espera del proveedor, sin colchón, porque un producto que vende siempre lo mismo no tiene de qué protegerse.',
    sku: DEMO_1,
    // ⚠️ Este paso medía el ERROR del motor (WAPE) y daba 0 %, que era el "control del
    // control". Se cambió el 2026-09-01: el error dejó de mostrarse en todo el producto
    // (ver el CLAUDE.md), y un recorrido que lo enseña como si fuera un dato para el usuario
    // contradice esa decisión. La idea del paso —"esto se puede verificar con una
    // calculadora"— se conserva con la demanda y el reorden, que son los números que sí se
    // usan para comprar.
    medir: medirProducto(DEMO_1, [
      ['demandaDiaria', 'Demanda proyectada', '/día'],
      ['puntoReorden', 'Punto de reorden', ' uds'],
    ]),
  },
  {
    id: 'espera',
    titulo: 'El proveedor tarda más',
    premisa:
      'Le cambiamos el tiempo de entrega a DEMO-1: de 30 días a 120. No cambia cuánto vende — cambia cuánto hay que anticiparse.',
    accion: 'Poner la espera en 120 días',
    esperado:
      'El punto de reorden se dispara: hay que pedir mucho antes. Y la cantidad sube, porque el pedido tiene que cubrir esos 120 días.',
    sku: DEMO_1,
    medir: medirProducto(DEMO_1, [
      ['puntoReorden', 'Punto de reorden', ' uds'],
      ['cantidadSugerida', 'Cuánto comprar', ' uds'],
    ]),
  },
  {
    id: 'servicio',
    titulo: 'Prometer más no siempre cuesta más',
    premisa:
      'El nivel de servicio es la promesa: "de cada 100 pedidos quiero poder atender 97". Lo subimos a 99, que debería pedir más colchón.',
    // ⚠️ Este paso se escribió al revés y la propia pantalla lo corrigió: decía "el colchón
    // sube". NO sube — el de DEMO-2 ya está exactamente en su tope (7,47 x 30 = 224), así que
    // pedir más servicio no mueve nada. Contar el tope es mucho más útil que fingir que no
    // existe: es una de las cuatro correcciones que este motor ya pagó en producción.
    esperado:
      'Ninguno de los dos colchones se mueve, y por razones distintas. DEMO-1 no varía nunca, así que no necesita colchón. DEMO-2 ya está en su TOPE: el motor no compra colchón infinito para algo que vende a saltos.',
    accion: 'Subir el nivel de servicio a 99 %',
    sku: DEMO_2,
    medir: (a, d) => [
      { concepto: 'Nivel de servicio', antes: a.settings.serviceLevel, despues: d.settings.serviceLevel, sufijo: ' %' },
      { concepto: 'Colchón de DEMO-2', antes: dato(a, DEMO_2, 'stockSeguridad'), despues: dato(d, DEMO_2, 'stockSeguridad'), sufijo: ' uds' },
      // Se muestra el tope al lado para que se vea que el colchón está pegado a él y no
      // "no reaccionó": son dos cosas distintas y la diferencia es todo el punto del paso.
      {
        concepto: 'Su tope (demanda × cobertura)',
        antes: Math.round(dato(a, DEMO_2, 'demandaDiaria') * a.settings.coverageTargetDays),
        despues: Math.round(dato(d, DEMO_2, 'demandaDiaria') * d.settings.coverageTargetDays),
        sufijo: ' uds',
      },
      { concepto: 'Colchón de DEMO-1', antes: dato(a, DEMO_1, 'stockSeguridad'), despues: dato(d, DEMO_1, 'stockSeguridad'), sufijo: ' uds' },
    ],
  },
  {
    id: 'cobertura',
    titulo: 'Cuánto stock querés tener',
    premisa:
      'La cobertura objetivo es cuántos días de venta querés tener en el depósito además de lo que se consume esperando. La subimos de 30 a 60.',
    accion: 'Subir la cobertura a 60 días',
    esperado:
      'Sube cuánto comprar en los dos. Y ahora SÍ sube el colchón de DEMO-2: al subir la cobertura sube su tope, que era lo que lo tenía frenado en el paso anterior.',
    medir: (a, d) => [
      { concepto: 'Cobertura objetivo', antes: a.settings.coverageTargetDays, despues: d.settings.coverageTargetDays, sufijo: ' días' },
      { concepto: 'Comprar DEMO-1', antes: dato(a, DEMO_1, 'cantidadSugerida'), despues: dato(d, DEMO_1, 'cantidadSugerida'), sufijo: ' uds' },
      { concepto: 'Comprar DEMO-2', antes: dato(a, DEMO_2, 'cantidadSugerida'), despues: dato(d, DEMO_2, 'cantidadSugerida'), sufijo: ' uds' },
      { concepto: 'Colchón de DEMO-2 (se destrabó)', antes: dato(a, DEMO_2, 'stockSeguridad'), despues: dato(d, DEMO_2, 'stockSeguridad'), sufijo: ' uds' },
    ],
  },
  {
    id: 'comprar',
    titulo: 'Decidir la compra',
    premisa:
      'Hasta acá el sistema recomienda. Ahora se aprueba una de esas recomendaciones y queda registrada como orden de compra, con su número y su fecha estimada.',
    accion: 'Aprobar la compra de DEMO-2',
    esperado: 'Aparece una orden en Órdenes de Compra. Nace en borrador: no se le manda nada a ningún proveedor.',
    medir: () => [],
  },
];

/** El paso final no mide: deja todo como estaba para poder recorrerlo de nuevo. */
export const RESET = {
  leadTimeDays: 30,
  serviceLevel: 97,
  coverageTargetDays: 30,
};
